import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import useGeolocation from '../hooks/useGeolocation';
import LiveTracking from './LiveTracking';
import { isIOS } from '../lib/capacitor';
import { API_URL } from '../lib/apiUrl';
import './ShareRide.css';

// Check if tier is a paid subscription
const isSubscriber = (tier) => ['starter', 'starter_annual', 'pro', 'pro_annual'].includes(tier);

// Check if tier is Pro (for live tracking)
const isProSubscriber = (tier) => ['pro', 'pro_annual'].includes(tier);

export default function ShareRide({ result, onClose }) {
  const { tier, getAccessToken } = useAuth();
  const toast = useToast();
  const { getLocation } = useGeolocation();
  const [isSharing, setIsSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shortCode, setShortCode] = useState(null);
  const [showLiveTracking, setShowLiveTracking] = useState(false);
  const [hasShared, setHasShared] = useState(false);

  const hasSubscription = isSubscriber(tier);
  const hasProSubscription = isProSubscriber(tier);

  const vehicleInfo = result?.vehicle_info;
  const vehicleDesc = vehicleInfo
    ? `${vehicleInfo.year || ''} ${vehicleInfo.make || ''} ${vehicleInfo.model || ''}`.trim()
    : 'Unknown Vehicle';

  const buildQuickShareText = (location, includeImageUrl = false) => {
    let text = `I'm in a rideshare!\n\n`;
    text += `Plate: ${result.plate} (${result.state})\n`;
    text += `Vehicle: ${vehicleDesc}\n`;

    if (result.is_tlc_vehicle === true) {
      const registry = result.city === 'chicago' ? 'Chicago BACP' : 'NYC TLC';
      text += `Status: Found in ${registry} Registry\n`;
    } else if (result.is_tlc_vehicle === false) {
      const registry = result.city === 'chicago' ? 'Chicago BACP' : 'NYC TLC';
      text += `Status: No record found in ${registry} Registry\n`;
    } else {
      text += `Status: Vehicle info only (no registry verification for ${result.state})\n`;
    }

    if (includeImageUrl && result.car_image) {
      text += `\nVehicle Photo: ${result.car_image}\n`;
    }

    if (location?.locationName) {
      text += `\nLocation: ${location.locationName}\n`;
    }

    if (location?.latitude && location?.longitude) {
      text += `\nMap: https://maps.google.com/maps?q=${location.latitude},${location.longitude}\n`;
    }

    text += `\nChecked via RideshareChecker.com`;
    return text;
  };

  // Convert image URL to a File object for sharing (via backend proxy to bypass CORS)
  const fetchImageAsFile = async (imageUrl) => {
    try {
      // Use backend proxy to bypass CORS restrictions on external images
      const proxyUrl = `${API_URL}/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }
      const blob = await response.blob();
      const fileName = 'vehicle.png';
      return new File([blob], fileName, { type: blob.type || 'image/png' });
    } catch (error) {
      return null;
    }
  };

  const handleQuickShare = async () => {
    setIsSharing(true);

    try {
      // Get current location
      const location = await getLocation();
      // Only include image URL for subscribers (desktop)
      const shareText = buildQuickShareText(location, hasSubscription);

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      // On mobile, try to share with image (subscribers only)
      if (isMobile && navigator.share && result.car_image && hasSubscription) {
        try {
          const imageFile = await fetchImageAsFile(result.car_image);

          if (imageFile) {
            const shareData = {
              title: 'My Rideshare Trip',
              text: shareText,
              files: [imageFile]
            };

            // Check if we can share this data
            const canShare = navigator.canShare ? navigator.canShare(shareData) : true;

            if (canShare) {
              await navigator.share(shareData);
              toast.success('Shared successfully!');
              setHasShared(true);
              return;
            }
          }
        } catch (imageError) {
          // Continue to text-only fallback
        }
      }

      // Text-only share for mobile (free users or fallback)
      if (isMobile && navigator.share) {
        try {
          await navigator.share({
            title: 'My Rideshare Trip',
            text: shareText,
          });
          toast.success('Shared successfully!');
          setHasShared(true);
          return;
        } catch (shareError) {
          if (shareError.name === 'AbortError') {
            return; // User cancelled
          }
        }
      }

      // Desktop or final fallback: copy to clipboard (include image URL for desktop)
      const desktopText = buildQuickShareText(location, hasSubscription);
      await navigator.clipboard.writeText(desktopText);
      toast.success('Trip details copied to clipboard!');
      setHasShared(true);

    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled share - that's ok
      } else {
        toast.error('Could not share. Please try again.');
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleCreateLink = async () => {
    setIsSharing(true);

    try {
      // Get current location and token
      const [location, token] = await Promise.all([
        getLocation(),
        getAccessToken()
      ]);

      const response = await fetch(`${API_URL}/api/shared-trips`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plate: result.plate,
          state: result.state,
          is_licensed: result.is_tlc_vehicle,
          vehicle_info: result.vehicle_info,
          car_image: result.car_image,
          tlc_data: result.tlc_data,
          latitude: location?.latitude,
          longitude: location?.longitude,
          location_name: location?.locationName
        })
      });

      const data = await response.json();

      if (response.ok && data.share_url) {
        setShareUrl(data.share_url);
        setShortCode(data.short_code);
        setShowLiveTracking(true);

        // Try to share the link
        if (navigator.share) {
          try {
            await navigator.share({
              title: 'Track My Rideshare Trip',
              url: data.share_url,
            });
            toast.success('Shared successfully! Enable live tracking below.');
            setHasShared(true);
          } catch (shareError) {
            if (shareError.name !== 'AbortError') {
              // Share was cancelled, just show the URL
              toast.info('Link created! Enable live tracking below.');
            }
          }
        } else {
          // No Web Share API, copy to clipboard
          try {
            await navigator.clipboard.writeText(data.share_url);
            toast.success('Share link copied to clipboard!');
          } catch (clipboardError) {
            toast.info('Link created! Use the Copy button below.');
          }
        }
      } else {
        toast.error(data.error || 'Could not create share link');
      }
    } catch (error) {
      toast.error('Could not create share link. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLink = async () => {
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      } catch (error) {
        toast.error('Could not copy to clipboard');
      }
    }
  };

  // Handle email share (desktop)
  const handleEmailShare = () => {
    if (shareUrl) {
      const subject = encodeURIComponent('Track My Rideshare Trip');
      const body = encodeURIComponent(`I'm in a rideshare! Track my trip here:\n\n${shareUrl}`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
  };

  // Handle "I Arrived Safely" - uses native share
  const handleArrivedSafely = async () => {
    const message = "I arrived safely! Just letting you know.";

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'I Arrived Safely',
          text: message,
        });
        toast.success('Message sent!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to clipboard
          await navigator.clipboard.writeText(message);
          toast.success('Message copied to clipboard!');
        }
      }
    } else {
      // Desktop fallback
      await navigator.clipboard.writeText(message);
      toast.success('Message copied to clipboard!');
    }
  };

  return (
    <div className="share-ride-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Share ride">
      <div className="share-ride-modal" onClick={e => e.stopPropagation()}>
        <button className="share-close-btn" onClick={onClose} aria-label="Close share dialog">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <h2 className="share-title">Share My Ride</h2>
        <p className="share-subtitle">Let someone know about your trip</p>

        <div className="share-vehicle-info">
          <div className="share-plate">{result.plate}</div>
          <div className="share-vehicle-desc">{vehicleDesc || 'Vehicle'}</div>
          {result.is_tlc_vehicle === true && (
            <span className="status-badge badge-licensed">In Registry</span>
          )}
          {result.is_tlc_vehicle === false && (
            <span className="status-badge badge-not-licensed">No Record</span>
          )}
          {result.is_tlc_vehicle === null && (
            <span className="status-badge badge-unverified">Vehicle Info Only</span>
          )}
        </div>

        <div className="share-options">
          <button
            className="share-option-btn quick-share"
            onClick={handleQuickShare}
            disabled={isSharing}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
            <span className="share-option-text">
              <strong>Quick Share</strong>
              <small>{hasSubscription ? 'Text message with photo & location' : 'Text message with location'}</small>
            </span>
          </button>

          {hasSubscription ? (
            <button
              className="share-option-btn create-link"
              onClick={handleCreateLink}
              disabled={isSharing}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              <span className="share-option-text">
                <strong>Create Link</strong>
                <small>Full details page with photo</small>
              </span>
            </button>
          ) : !isIOS() && (
            <Link to="/pricing" className="share-option-btn create-link locked" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              <span className="share-option-text">
                <strong>Create Link</strong>
                <small>Subscriber feature - Upgrade to unlock</small>
              </span>
            </Link>
          )}
        </div>

        {showLiveTracking && shortCode && shareUrl && hasProSubscription && (
          <LiveTracking
            shortCode={shortCode}
            shareUrl={shareUrl}
            onClose={() => setShowLiveTracking(false)}
          />
        )}

        {shareUrl && !showLiveTracking && (
          <div className="share-link-section">
            <p className="share-link-label">Your shareable link:</p>
            <div className="share-url-container">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="share-url-input"
                onClick={(e) => e.target.select()}
              />
            </div>
            <div className="share-link-buttons">
              <button className="share-link-btn copy" onClick={handleCopyLink}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Copy Link
              </button>
              <button className="share-link-btn email" onClick={handleEmailShare}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                Email
              </button>
              {hasProSubscription ? (
                <button
                  className="share-link-btn track"
                  onClick={() => setShowLiveTracking(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  Live Track
                </button>
              ) : !isIOS() && (
                <Link
                  to="/pricing"
                  className="share-link-btn pro"
                  onClick={onClose}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                    <path d="M2 17l10 5 10-5"></path>
                    <path d="M2 12l10 5 10-5"></path>
                  </svg>
                  Pro
                </Link>
              )}
            </div>
          </div>
        )}

        {isSharing && (
          <div className="share-loading">
            <div className="share-spinner"></div>
            <span>Getting your location...</span>
          </div>
        )}

        {/* Arrived Safely button - shows after sharing */}
        {hasShared && (
          <button
            className="arrived-safely-btn"
            onClick={handleArrivedSafely}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            I Arrived Safely
          </button>
        )}

        {!showLiveTracking && (
          <p className="share-note">
            Link expires in 24 hours. Location included for safety.
          </p>
        )}
      </div>
    </div>
  );
}
